import React from "react";
import styles from "./detailed-transaction-history-filter.module.scss";
import moment from "moment";
import { isDate } from "lodash";
import { Autocomplete, Field, Filter, DatePicker, Button } from "components/commons";
import TimePickerV2 from "components/commons/time-picker/time-picker-v2";
import { locale } from "localization/en";
import SearchTypeField from "components/commons/search-field/search-type";
import { combineDateAndTimeInput, today12AMtime } from "utils";

const TIME_PLACEHOLDER = "12:00 AM";

const isSameDay = (a, b) => isDate(a) && isDate(b) && moment(a).isSame(b, "day");

const DetailedTransactionHistoryFilter = ({
  filterState,
  modifyFilter,
  filterCount,
  applyFilter,
  applyClearFilter,
  resetFilter,
  exportReport,
  exporting,
  searchKey,
  modifySearch,
  applySearch,
  applyClearSearch,
}) => {
  const searchType = [
    { label: locale.salesInvoiceNo, value: "salesInvoiceNo" },
    { label: locale.productName, value: "productName" },
    { label: locale.paymentReferenceNumber, value: "paymentReferenceNumber" },
    { label: locale.paymentReferenceName, value: "refName" },
  ];

  const dateType = [
    { label: locale.transactionDate, value: "transactionDate" },
    { label: locale.shiftDate, value: "shiftDate" },
  ];

  const now = new Date();
  const { startDate, endDate } = filterState;

  const startMaxTime = isSameDay(startDate, endDate)
    ? endDate
    : isSameDay(startDate ?? now, now)
    ? now
    : undefined;
  const endMinTime = isSameDay(endDate ?? now, startDate) ? startDate : undefined;
  const endMaxTime = isSameDay(endDate ?? now, now) ? now : undefined;

  const changeStartDate = (value) => {
    const max = endDate ?? now;
    modifyFilter({ startDate: value > max ? max : value });
  };

  const changeEndDate = (value) => {
    const capped = value > now ? now : value;
    modifyFilter({ endDate: startDate && capped < startDate ? startDate : capped });
  };

  return (
    <div className={styles?.container}>
      <div className={styles?.filter}>
        <Filter
          close={resetFilter}
          clear={applyClearFilter}
          submit={() => {
            applyFilter();
          }}
          filterCount={filterCount}
        >
          <Field label={locale.filterDateBy} className={styles.fieldSelect}>
            <Autocomplete
              name="dateType"
              value={filterState.dateType}
              options={dateType}
              onChange={(name, { value }) => {
                modifyFilter({ [name]: value });
              }}
            />
          </Field>
          <Field label={locale.startDateAndTime} childrenClassName={styles.dateTime}>
            <DatePicker
              name="startDate"
              value={startDate}
              placeholder={locale.startDate}
              allowPastDates
              disableEndDate={endDate ?? now}
              onChange={(_, { value }) => {
                if (isDate(value)) {
                  changeStartDate(combineDateAndTimeInput(value, startDate ?? today12AMtime()));
                }
              }}
            />
            <TimePickerV2
              name="startTime"
              value={startDate}
              date={startDate ?? now}
              placeholder={TIME_PLACEHOLDER}
              variant={"purple"}
              maxTime={startMaxTime}
              onChange={(_, { value }) => {
                if (isDate(value)) {
                  changeStartDate(combineDateAndTimeInput(startDate ?? now, value));
                }
              }}
            />
          </Field>
          <Field label={locale.endDateAndTime} childrenClassName={styles.dateTime}>
            <DatePicker
              name="endDate"
              value={endDate}
              placeholder={locale.endDate}
              allowPastDates
              disableStartDate={startDate}
              disableEndDate={now}
              onChange={(_, { value }) => {
                if (isDate(value)) {
                  changeEndDate(
                    combineDateAndTimeInput(value, endDate ?? moment(value).endOf("day").toDate())
                  );
                }
              }}
            />
            <TimePickerV2
              name="endTime"
              value={endDate}
              date={endDate ?? now}
              placeholder={TIME_PLACEHOLDER}
              variant={"purple"}
              minTime={endMinTime}
              maxTime={endMaxTime}
              onChange={(_, { value }) => {
                if (isDate(value)) {
                  changeEndDate(combineDateAndTimeInput(endDate ?? now, value));
                }
              }}
            />
          </Field>
        </Filter>
        <Field className={styles.search}>
          <SearchTypeField
            placeholder={locale.keyword}
            value={searchKey}
            searchWithSymbols={false}
            restrict={false}
            onChange={(_, { value }) => {
              modifySearch(value);
            }}
            onEnter={applySearch}
            onClear={applyClearSearch}
            searchKeyName={"searchType"}
            searchKeyValue={filterState.searchType}
            searchKeyOptions={searchType}
            searchKeyOnChange={(name, { value }) => {
              modifyFilter({ [name]: value });
            }}
          />
        </Field>
        <Field className={styles.downloadButton}>
          <Button
            startIcon={<span className="icon-download" />}
            exportButton
            onClick={exportReport}
            disabled={exporting}
            loading={exporting}
          >
            {locale.export}
          </Button>
        </Field>
      </div>
    </div>
  );
};

export default DetailedTransactionHistoryFilter;
